import ReportContent from '@app/components/reports/components/report-content';
import { HighestEmissionsItemsCard } from '@app/components/reports/components/report-highest-emissions-items';
import ReportMetric from '@app/components/reports/components/report-metric';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import {
  EmissionsCategoryEnum,
  OperationReportTypeEnum,
} from '@shared/api/types';
import { itemsByRatingChartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import {
  RatingToTextEnum,
  roundNumberToNDecimalPlaces,
} from '@shared/lib/utils';

interface FoodItemsProps extends ReportSectionComponent {}

export default function FoodItems({
  reportType,
  downloadMode,
}: FoodItemsProps) {
  const {
    result,
    result: { foodAndDrink },
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);

  const data = {
    totalItems: foodAndDrink?.totalItems,
    totalRecords:
      reportType === OperationReportTypeEnum.purchase
        ? result.foodAndDrink?.totalPurchases
        : result.foodAndDrink?.totalSales,
    highest_emissions_items: foodAndDrink?.highestEmissionItems,
    items_by_rating: foodAndDrink?.itemsByRating,
  };

  const chartData = data.items_by_rating?.map((category) => {
    return {
      category: category.rating,
      items: category.numberOfItems,
      fill: `var(--rating-${category.rating.toLowerCase()})`,
    };
  });

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          {reportType === OperationReportTypeEnum.purchase
            ? 'To start reducing your emissions, we recommend analysing the highest-emission items you purchased and looking to reduce the amount you purchase in favour of a less carbon-intensive alternative, where possible.'
            : 'To start reducing your emissions, we recommend analysing the highest-emission items you sell and looking to reduce their carbon intensity or promote less carbon-intensive alternatives, where possible.'}
        </Text>
        <div className="flex">
          <ReportMetric
            title="Total items"
            value={roundNumberToNDecimalPlaces(data.totalItems ?? 0, 0)}
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title={`Total ${reportTypeCopy.plural}`}
            value={roundNumberToNDecimalPlaces(data.totalRecords ?? 0, 0)}
            size="md"
            className="flex-1"
          />
        </div>
        <div className="space-y-2">
          <Text variant="h4">Items by carbon rating</Text>
          <Text variant="reportBody">
            This chart shows the distribution of items {reportTypeCopy.past},
            broken down by their carbon rating.
          </Text>
          <div className="flex items-center">
            <SimplePieChart
              dataKey="items"
              nameKey="category"
              chartConfig={itemsByRatingChartConfig}
              chartData={chartData!.reverse()}
              innerRadius={60}
              className="size-72"
            />

            <div className="flex h-full flex-col gap-y-3">
              {(
                ['A', 'B', 'C', 'D', 'E'] as Array<
                  keyof typeof RatingToTextEnum
                >
              ).map((rating, idx) => {
                return (
                  <div key={idx} className="flex items-center gap-4">
                    <RatingCloud
                      rating={rating as EmissionsCategoryEnum}
                      format={downloadMode ? 'png' : 'svg'}
                    />
                    <Text variant="reportBody">{RatingToTextEnum[rating]}</Text>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
      <>
        <Text variant="h3">Highest emission items</Text>
        <div className="space-y-3">
          <div className="grid grid-cols-11 gap-x-4">
            <Text className="col-span-3">Food or drink item</Text>
            <Text className="col-span-2 justify-self-center">Rating</Text>
            <span className="col-span-2 text-center">
              <Text>Total {reportTypeCopy.past}</Text> <Unit variant="kg" />
            </span>
            <span className="col-span-2 text-center">
              <Text>Total emissions</Text>
              <Unit variant="tCO2e" />
            </span>
            <Text className="col-span-2 text-center">% of emissions</Text>
          </div>
          {data.highest_emissions_items?.map((item, idx) => {
            return (
              idx < 6 && (
                <HighestEmissionsItemsCard
                  key={idx}
                  item={item.name}
                  rating={item.rating as EmissionsCategoryEnum}
                  totalPurchased={item.weightKg}
                  totalEmissions={item.emissionsTonnes}
                  pctOfEmissions={item.pctOfTotalEmissions}
                  downloadMode={downloadMode!}
                />
              )
            );
          })}
        </div>
      </>
    </ReportContent>
  );
}
